// frontend/src/pages/visitor/Photos.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Image, AlertCircle } from 'react-feather';
import VisitorLayout from '../../components/layouts/VisitorLayout';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import CoupleService from '../../services/couple.service';
import PhotoService from '../../services/photo.service';
import UploadService from '../../services/upload.service';
import { Couple, Photo } from '../../types';

const Photos: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const [couple, setCouple] = useState<Couple | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track which images failed to load
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  
  // Photo viewer modal
  const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);
  const [currentPhoto, setCurrentPhoto] = useState<Photo | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      if (!customUrl) return;

      try {
        setLoading(true);
        
        // Fetch couple by custom URL
        const coupleData = await CoupleService.getCoupleByCustomUrl(customUrl);
        setCouple(coupleData);
        
        if (coupleData) {
          // Fetch all photos, not just featured ones
          const photosData = await PhotoService.getCouplePhotos(coupleData._id);
          setPhotos(photosData);
        }
        
        setError(null);
        
        // Reset failed images when fetching new photos
        setFailedImages({});
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load photos';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customUrl]);
  
  // Get an optimized image URL for display
  const getDisplayUrl = (url: string): string => {
    return UploadService.getOptimizedImageUrl(url);
  };
  
  const handleImageError = (photoId: string) => {
    setFailedImages(prev => ({
      ...prev,
      [photoId]: true
    }));
  };
  
  const openPhotoViewer = (photo: Photo, index: number) => {
    setCurrentPhoto(photo);
    setCurrentIndex(index);
    setIsViewerOpen(true);
  };
  
  const navigatePhoto = (direction: 'prev' | 'next') => {
    if (photos.length === 0) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = (currentIndex - 1 + photos.length) % photos.length;
    } else {
      newIndex = (currentIndex + 1) % photos.length;
    }
    
    setCurrentPhoto(photos[newIndex]);
    setCurrentIndex(newIndex);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader size="lg" label="Loading photos..." />
      </div>
    );
  }

  if (error || !couple) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Image className="mx-auto h-12 w-12 text-pink-500" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Photos Not Found</h2>
          <p className="mt-1 text-sm text-gray-500">
            {error || "We couldn't find the photos you're looking for."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <VisitorLayout couple={couple}>
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Photo Gallery</h2>
            <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-500 sm:mt-4">
              Relive our special moments through these photographs
            </p>
          </div>
          
          {photos.length === 0 ? (
            <div className="mt-12 text-center py-10 bg-gray-50 rounded-lg">
              <Image className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No photos yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Check back later for wedding photos.
              </p>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8">
              {photos.map((photo, index) => (
                <div 
                  key={photo._id} 
                  className="group relative cursor-pointer"
                  onClick={() => openPhotoViewer(photo, index)}
                >
                  <div className="w-full bg-gray-200 rounded-lg overflow-hidden aspect-w-3 aspect-h-2">
                    {failedImages[photo._id] ? (
                      <div className="h-full w-full flex items-center justify-center">
                        <div className="text-center p-4">
                          <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Image could not be loaded</p>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={getDisplayUrl(photo.url)}
                        alt={photo.title || 'Wedding photo'}
                        className="w-full h-full object-center object-cover group-hover:opacity-75"
                        onError={() => handleImageError(photo._id)}
                        loading="lazy"
                      />
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity" />
                  </div>
                  {(photo.title || photo.caption) && (
                    <div className="mt-4 flex justify-between">
                      <div>
                        {photo.title && (
                          <h3 className="text-sm text-gray-700">
                            {photo.title}
                          </h3>
                        )}
                        {photo.caption && (
                          <p className="mt-1 text-sm text-gray-500">{photo.caption}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Photo Viewer Modal */}
      <Modal
        open={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        title=""
        size="xl"
      >
        {currentPhoto && (
          <div className="relative">
            <div className="aspect-w-16 aspect-h-9 sm:aspect-w-3 sm:aspect-h-2">
              {failedImages[currentPhoto._id] ? (
                <div className="h-full w-full flex items-center justify-center bg-gray-100">
                  <div className="text-center p-4">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">Image could not be loaded</p>
                  </div>
                </div>
              ) : (
                <img
                  src={getDisplayUrl(currentPhoto.url)}
                  alt={currentPhoto.title || 'Wedding photo'}
                  className="object-contain w-full h-full"
                  onError={() => handleImageError(currentPhoto._id)}
                />
              )}
            </div>
            
            <div className="absolute inset-x-0 bottom-0 px-4 py-3 bg-black bg-opacity-50">
              <div className="text-white">
                {currentPhoto.title && (
                  <h3 className="text-lg font-medium">{currentPhoto.title}</h3>
                )}
                {currentPhoto.caption && (
                  <p className="text-sm opacity-90">{currentPhoto.caption}</p>
                )}
              </div>
            </div>
            
            {/* Navigation buttons */}
            <button
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full p-2 text-white hover:bg-opacity-75 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                navigatePhoto('prev');
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full p-2 text-white hover:bg-opacity-75 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                navigatePhoto('next');
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </Modal>
    </VisitorLayout>
  );
};

export default Photos;